import type ApolloCache from '@wora/apollo-cache'
import type { CompanyQuery, Company, SearchCompaniesQuery } from '../generated/types'
import { CompanyDocument, SearchCompaniesDocument, FullCompanyFragmentDoc } from '../generated/types'
import { defaultDataIdFromObject } from 'apollo-cache-inmemory'
import type { CacheProxy } from '.'
import { updateQueries } from './util'

/**
 * Updates any cached companies in the apollo cache
 */
export function updateCachedCompany(proxy: CacheProxy, company: Company) {
  const cache = proxy as ApolloCache
  cache.writeQuery({
    query: CompanyDocument,
    variables: { id: company._id },
    data: {
      company: {
        __typename: 'Company',
        ...company
      }
    }
  })

  // update all searchCompany queries to remove the company
  if (company._deleted) {
    updateQueries<SearchCompaniesQuery>(cache, {
      query: SearchCompaniesDocument,
      update: (data) => {
        const filteredCompanies = data.searchCompanies.companies.filter((q) => q._id !== company._id)

        return {
          ...data,
          searchCompanies: {
            ...data.searchCompanies,
            companies: filteredCompanies,
            total_rows:
              filteredCompanies.length !== data.searchCompanies.companies.length
                ? data.searchCompanies.total_rows - 1
                : data.searchCompanies.total_rows
          }
        }
      }
    })
  }

  cache.writeFragment({
    fragment: FullCompanyFragmentDoc,
    id: defaultDataIdFromObject({ id: company._id, __typename: 'Company' }),
    data: {
      __typename: 'Company',
      ...company
    }
  })
}

export function updateCachedCompanyPartially(cache: ApolloCache, companyId: string, values: Partial<Company>) {
  try {
    const company = cache.readQuery<CompanyQuery>({
      query: CompanyDocument,
      variables: { id: companyId }
    }).company

    updateCachedCompany(cache, {
      ...company,
      ...values
    })
  } catch {
    console.warn('Unable to find company for id ' + companyId)
  }
}
